import { Button, CloseIcon, JoonUIColor, Typography } from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import useMailboxStore from "./hooks/useMailboxStore"
import { useSendMailModalStore } from "./hooks/useSendMailModalStore"
import MailAudioButton from "./MailAudioButton"
import {
  SmallImageModal,
  useSmallImageModalStore,
} from "../../../../components/modals/SmallImageModal"
import SlideUpModal from "../../../../components/slideUpModal/SlideUpModal"
import { useFamilyQuery } from "../../../../networking/queries"
import { FamilyMail, FamilyQuerySelect } from "../../../../types"
import { getChildFromMail } from "../../../../util/mail"
import { getChildImage } from "../../../../util/util"

interface ViewMailModalStoreProps {
  isOpen: boolean
  onOpen: (mail: FamilyMail) => void
  onClose: () => void
  mail: FamilyMail | null
}

export const useViewMailModalStore = create<ViewMailModalStoreProps>((set) => ({
  isOpen: false,
  onOpen: (mail) => set({ isOpen: true, mail }),
  onClose: () => set({ isOpen: false }),
  mail: null,
}))

const ViewMailModal = () => {
  const { onOpen: onOpenImageModal } = useSmallImageModalStore()
  const { selectedUserId } = useMailboxStore()
  const { onOpen: onOpenSendMailModal } = useSendMailModalStore()
  const { isOpen, onClose, mail } = useViewMailModalStore()
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)
  const { t } = useTranslation()
  const childProfile = children?.find(
    (child) => child.user.id === selectedUserId
  )

  const onReplyClick = () => {
    if (!mail) return
    onOpenSendMailModal(mail.sender.id)
    onClose()
  }

  if (!mail || !childProfile?.user) return null

  const childUser = getChildFromMail(mail, childProfile.user.id)
  const isInboxIn = mail.sender.id === childUser.id
  const childImage = getChildImage(childProfile?.user)
  const messageTime = dayjs(mail.sent_datetime).format("MMM D, h:mm A")
  const photoAttachmentUrl = mail.photo_attachment_url || ""
  const audioAttachmentUrl = mail.audio_attachment_url || ""

  return (
    <SlideUpModal
      isOpen={isOpen}
      onClose={onClose}
      showCard={true}
      size="fit-content"
      backgroundType="overlay"
      zIndex={1001}
    >
      <div className="flex flex-col p-4 pt-6 gap-2">
        <button className="absolute right-4 top-4 p-2" onClick={onClose}>
          <CloseIcon color={JoonUIColor.icon.neutral} />
        </button>
        <div className="flex flex-row gap-2 justify-between items-center w-full">
          <img
            src={childImage}
            className="w-10 h-10 rounded-full"
            alt={childUser.name}
          />
          <div className="flex flex-col flex-1 items-start">
            <Typography variant="bodyBold" className="flex-1">
              {isInboxIn
                ? t("MAIL_FROM", { name: mail.sender.name })
                : t("MAIL_TO", { name: mail.receiver.name })}
            </Typography>
            <Typography variant="bodyXSmall">{messageTime}</Typography>
          </div>
        </div>
        <Typography variant="bodySmall" className="w-full text-left">
          {mail.body}
        </Typography>
        <div className="flex flex-row gap-2 items-center justify-start">
          {photoAttachmentUrl && (
            <button onClick={() => onOpenImageModal(photoAttachmentUrl)}>
              <img
                className="w-[50px] h-[50px] rounded-md border border-borderPrimary object-cover"
                src={photoAttachmentUrl}
                alt={photoAttachmentUrl}
              />
            </button>
          )}
          {mail.shop_item_gift && (
            <div className="flex flex-row w-[50px] h-[50px] items-center justify-center rounded-md border border-borderPrimary">
              <img
                className="flex-1"
                src={mail.shop_item_gift.image_url}
                alt="Gift"
              />
            </div>
          )}
          {audioAttachmentUrl && (
            <MailAudioButton audioUrl={audioAttachmentUrl} />
          )}
        </div>
        {isInboxIn && (
          <Button
            text={t("MAIL_REPLY_TO", { name: mail.sender.name })}
            onClick={onReplyClick}
            fullWidth
          />
        )}
      </div>
      <SmallImageModal />
    </SlideUpModal>
  )
}

export default ViewMailModal
